import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MANUAL_ROSTER_ITEM_DEFAULT_VALUES, TRosterUserType } from '../onboarding/organization/C_RosterManualEntryForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { rosterUsersSchema } from '../onboarding/organization/validations';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import RosterCSVFile from '../onboarding/organization/C_RosterCSVFile';
import { TParsedRosterList, TPeerSupportCertificateInfoMap } from '@/store/useOrganizationOnboardingStore';
import RosterPreview from '../onboarding/organization/D_RosterPreview';
import onboardingMessages from '@/messages/onboarding';
import useAddMemberToOrganization from '@/hooks/organization/addMemberToOrganization';
import { TUserInviteInfo } from '@/types/User.types';

interface Props {
  onMemberAddFinish: () => void;
  onGoBack: () => void;
}

const trimInviteValue = (value: string | undefined) => value?.trim() ?? '';

const OrgMembersRosterBulkAdd = ({ onMemberAddFinish, onGoBack }: Props) => {
  const [isTablePreview, setIsTablePreview] = useState(false);

  const [rosterCSVFile, setRosterCSVFile] = useState<File | null>(null);
  const [rosterCSVFileNamePreview, setRosterCSVFileNamePreview] = useState<string | null>(null);
  const [rosterList, setRosterList] = useState<TParsedRosterList>([]);
  const [customRosterError, setCustomRosterError] = useState('');

  const [peerSupportCertificateDataCSV, setPeerSupportCertificateDataCSV] = useState<TPeerSupportCertificateInfoMap>(
    new Map(),
  );

  // CSV-editable roster table data (probably not needed here)
  const methodsCSV = useForm<{
    rosterUsers?: TRosterUserType[];
  }>({
    defaultValues: {
      rosterUsers: rosterList ? rosterList : [MANUAL_ROSTER_ITEM_DEFAULT_VALUES],
    },
    mode: 'all',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(rosterUsersSchema),
  });

  const { isPending: isInvitePending, mutate: inviteMembersToOrg } = useAddMemberToOrganization(onMemberAddFinish);

  const submitNewMembers = useCallback(async () => {
    const usersToInvite: TUserInviteInfo[] =
      rosterList?.map((rosterItem) => ({
        email: trimInviteValue(rosterItem.Email).toLowerCase(),
        firstName: trimInviteValue(rosterItem['First Name']),
        lastName: trimInviteValue(rosterItem['Last Name']),
        userAffiliation: trimInviteValue(rosterItem.Affiliation).toUpperCase(),
        phone: trimInviteValue(rosterItem.Phone),
        isPeerSupport: rosterItem['Peer Support'],
      })) ?? [];

    inviteMembersToOrg(usersToInvite);
  }, [rosterList]);

  if (isTablePreview) {
    return (
      <VStack>
        <RosterPreview
          rosterList={rosterList}
          onGoBack={() => setIsTablePreview(false)}
          onSubmit={submitNewMembers}
          isLoading={isInvitePending}
          headerHidden={true}
        />
      </VStack>
    );
  }

  return (
    <VStack paddingBottom={'40px'}>
      <Text variant={'loraSmallTitle'}>Bulk Add Members to Your Organization</Text>
      <RosterCSVFile
        csvTabStep={0}
        selectedFile={rosterCSVFile}
        setSelectedFile={setRosterCSVFile}
        preview={rosterCSVFileNamePreview}
        setPreview={setRosterCSVFileNamePreview}
        setRoster={setRosterList}
        setCustomRosterError={setCustomRosterError}
        methods={methodsCSV}
        peerSupportData={peerSupportCertificateDataCSV}
        setPeerSupportData={setPeerSupportCertificateDataCSV}
        titleText={onboardingMessages.uploadUpdatedToYourRoster}
        descriptionText={onboardingMessages.uploadYourUpdatedRosterDescription}
        buttonsColor="primary"
      />

      <Text variant={'error'} marginTop={'10px'}>
        {customRosterError}
      </Text>

      <HStack width={'100%'} bg="background.separatorBlue" height={'2px'} marginTop={'54px'} marginBottom={'72px'} />

      <Button
        variant={'continue'}
        disabled={!rosterCSVFileNamePreview}
        isDisabled={!rosterCSVFileNamePreview}
        _hover={{ backgroundColor: !rosterCSVFileNamePreview ? 'primary.400' : 'primary.600' }}
        onClick={() => setIsTablePreview(true)}
      >
        Continue
      </Button>
      <Text
        onClick={onGoBack}
        cursor={'pointer'}
        marginY={'40px'}
        fontWeight={700}
        fontSize={'16px'}
        color={'text.mediumGray'}
        lineHeight={'18px'}
      >
        Go Back
      </Text>
    </VStack>
  );
};

export default OrgMembersRosterBulkAdd;
